import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Book, 
  BookOpen, 
  Search, 
  Sparkles,
  ChevronRight,
  Clock,
  Bookmark,
  Star,
  Loader2,
  Calendar,
  Share2,
  Copy,
  Twitter,
  Facebook
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ai-granth`;

const CATEGORY_ICONS = {
  veda: '📚',
  upanishad: '📿',
  purana: '📜',
  itihasa: '⚔️',
  gita: '🙏',
  stotra: '🕉️',
  chalisa: '🎵',
  aarti: '🔔',
  pooja_path: '🪔',
  vrat_katha: '🕯️',
  smriti: '⚖️',
  agama: '🔱',
  vedanga: '📐',
  other: '📖'
};

export default function AIGranthHomePage({ user }) {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dailyVerse, setDailyVerse] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [booksRes, categoriesRes, dailyRes] = await Promise.all([
        axios.get(`${API}/books`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/daily-verse`)
      ]);

      setBooks(booksRes.data.books || []);
      setCategories(categoriesRes.data.categories || []);
      setDailyVerse(dailyRes.data);

      if (user) {
        const token = localStorage.getItem('dharmbook_token');
        const bookmarksRes = await axios.get(`${API}/bookmarks?token=${token}`);
        setBookmarks(bookmarksRes.data.bookmarks || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/ai-granth/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const shareVerse = (platform) => {
    if (!dailyVerse?.verse) return;
    
    const verseText = dailyVerse.verse.original_text?.split('\n')[0] || '';
    const meaning = dailyVerse.verse.english_meaning || dailyVerse.verse.hindi_meaning || '';
    const source = `${dailyVerse.book?.title || 'Scripture'} - Verse ${dailyVerse.verse.verse_number}`;
    
    const shareText = `"${verseText}"\n\n${meaning}\n\n- ${source}\n\n🙏 Today's verse from DharmBook AI Granth`;
    const shareUrl = window.location.href;

    switch (platform) {
      case 'copy':
        navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
        toast.success('Verse copied to clipboard!');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`, '_blank');
        break;
      default:
        break;
    }
  };

  const filteredBooks = selectedCategory === 'all'
    ? books
    : books.filter(b => b.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl mb-4 animate-pulse">📖</div>
          <p className="text-amber-800">Loading sacred texts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      {/* Hero Section */}
      <div 
        className="relative py-12 px-4"
        style={{
          background: 'linear-gradient(135deg, #8B4513 0%, #654321 50%, #4A3728 100%)'
        }}
      >
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />

        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100/20 backdrop-blur-sm rounded-full mb-4">
              <Sparkles className="w-4 h-4 text-amber-300" />
              <span className="text-amber-200 text-sm">AI-Powered Scripture Library</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Noto Serif Devanagari, serif' }}>
              ग्रन्थ
            </h1>
            <h2 className="text-2xl text-amber-200 font-serif">AI Granth</h2>
            <p className="text-amber-100/80 mt-2">Interactive scripture reading with AI-powered insights</p>

            {/* Quick Actions */}
            {user && (
              <div className="flex items-center justify-center gap-3 mt-4">
                <Link
                  to="/ai-granth/guru"
                  className="flex items-center gap-2 px-5 py-2.5 bg-amber-500/30 hover:bg-amber-500/40 backdrop-blur-sm border border-amber-300/40 rounded-full text-white text-sm font-semibold transition-all"
                  data-testid="ask-guru-btn"
                >
                  <span style={{ fontFamily: "'Yatra One', serif" }}>&#x0950;</span>
                  Ask DharmGuru
                </Link>
                <Link
                  to="/ai-granth/bookmarks"
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-amber-200/30 rounded-full text-amber-100 text-sm transition-colors"
                  data-testid="my-bookmarks-btn"
                >
                  <Bookmark className="w-4 h-4" />
                  My Bookmarks
                </Link>
              </div>
            )}
          </div>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search within scriptures..."
                className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-sm border border-amber-200/30 rounded-2xl text-white placeholder-amber-200/60 focus:outline-none focus:ring-2 focus:ring-amber-300/50"
                data-testid="granth-search-input"
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-200/60" />
              <button
                onClick={handleSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-xl transition-colors"
                data-testid="granth-search-btn"
              >
                Search
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Verse */}
      {dailyVerse?.verse && (
        <div className="max-w-4xl mx-auto px-4 -mt-6 relative z-20">
          <div 
            className="bg-white rounded-2xl shadow-xl p-6 border border-amber-100"
            style={{
              background: 'linear-gradient(135deg, #FFFBF5 0%, #FFF8F0 100%)'
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">Verse of the Day</span>
                <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">{dailyVerse.date}</span>
              </div>
              
              {/* Share Buttons */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => shareVerse('copy')}
                  className="p-2 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors"
                  title="Copy verse"
                  data-testid="share-copy-btn"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={() => shareVerse('whatsapp')}
                  className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                  title="Share on WhatsApp"
                  data-testid="share-whatsapp-btn"
                >
                  <Share2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => shareVerse('twitter')}
                  className="p-2 text-blue-500 hover:bg-blue-100 rounded-lg transition-colors"
                  title="Share on Twitter"
                  data-testid="share-twitter-btn"
                >
                  <Twitter className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="mb-4">
              <p 
                className="text-lg text-amber-900 leading-relaxed"
                style={{ fontFamily: 'Noto Serif Devanagari, serif' }}
              >
                {dailyVerse.verse.original_text?.split('\n')[0]}
              </p>
              {dailyVerse.verse.transliteration && (
                <p className="text-sm text-amber-600 mt-2 italic">
                  {dailyVerse.verse.transliteration.split('\n')[0]}
                </p>
              )}
              {dailyVerse.verse.english_meaning && (
                <p className="text-sm text-amber-700 mt-2 bg-amber-50 p-3 rounded-lg">
                  "{dailyVerse.verse.english_meaning}"
                </p>
              )}
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-amber-100">
              <div className="text-sm text-amber-600">
                {dailyVerse.book?.title} • Chapter {dailyVerse.chapter?.chapter_number}, Verse {dailyVerse.verse.verse_number}
              </div>
              <Link
                to={`/ai-granth/book/${dailyVerse.book?.id || dailyVerse.verse.book_id}/chapter/${dailyVerse.chapter?.chapter_id || dailyVerse.verse.chapter_id}`}
                className="text-sm text-amber-700 hover:text-amber-900 font-medium flex items-center gap-1"
              >
                Read More <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Categories */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              selectedCategory === 'all'
                ? 'bg-amber-800 text-white shadow-md'
                : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
            }`}
            data-testid="category-all"
          >
            All Texts
          </button>
          {categories.filter(c => c.count > 0).map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                selectedCategory === cat.id
                  ? 'bg-amber-800 text-white shadow-md'
                  : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
              }`}
              data-testid={`category-${cat.id}`}
            >
              <span>{CATEGORY_ICONS[cat.id] || '📖'}</span>
              {cat.name_hindi || cat.name}
              <span className="text-xs opacity-75">({cat.count})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Continue Reading */}
      {bookmarks.length > 0 && (
        <div className="max-w-4xl mx-auto px-4 mb-8">
          <h2 className="text-lg font-semibold text-amber-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-600" />
            Continue Reading
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {bookmarks.slice(0, 2).map((bm) => (
              <Link
                key={bm.bookmark.id}
                to={`/ai-granth/book/${bm.book?.id}/chapter/${bm.chapter?.id}`}
                className="flex items-center gap-4 p-4 bg-white rounded-xl border border-amber-100 hover:shadow-md transition-all group"
              >
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center text-2xl">
                  📖
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-amber-900 truncate group-hover:text-amber-700 transition-colors">
                    {bm.book?.title}
                  </h3>
                  <p className="text-sm text-amber-600">
                    Chapter {bm.chapter?.chapter_number}, Verse {bm.verse?.verse_number}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-amber-400 group-hover:text-amber-600" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Books Grid */}
      <div className="max-w-4xl mx-auto px-4 pb-20">
        <h2 className="text-xl font-bold text-amber-900 mb-6 flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-amber-600" />
          Sacred Library
        </h2>

        {filteredBooks.length === 0 ? (
          <div className="text-center py-16">
            <Book className="w-16 h-16 text-amber-300 mx-auto mb-4" />
            <p className="text-amber-700">No texts found in this category</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBooks.map((book) => (
              <Link
                key={book.id}
                to={`/ai-granth/book/${book.id}`}
                className="group bg-white rounded-2xl shadow-sm border border-amber-100 overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1"
                data-testid={`book-card-${book.id}`}
              >
                {/* Cover */}
                <div className="relative h-40 bg-gradient-to-br from-amber-100 to-amber-50 overflow-hidden">
                  {book.cover_image ? (
                    <img 
                      src={book.cover_image} 
                      alt={book.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-6xl">{CATEGORY_ICONS[book.category] || '📖'}</span>
                    </div>
                  )}

                  {/* AI Badge */}
                  {book.is_ai_enabled && (
                    <div className="absolute top-3 right-3 px-2 py-1 bg-amber-600 text-white text-xs font-medium rounded-full flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      AI
                    </div>
                  )}

                  {/* Category Badge */}
                  <div className="absolute top-3 left-3 px-2 py-1 bg-white/90 text-amber-800 text-xs font-medium rounded-full">
                    {CATEGORY_ICONS[book.category]} {book.category}
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  {book.title_hindi && (
                    <p 
                      className="text-amber-600 text-lg mb-1"
                      style={{ fontFamily: 'Noto Serif Devanagari, serif' }}
                    >
                      {book.title_hindi}
                    </p>
                  )}
                  <h3 className="font-semibold text-amber-900 text-lg group-hover:text-amber-700 transition-colors">
                    {book.title}
                  </h3>
                  {book.author && (
                    <p className="text-sm text-amber-600 mt-1">by {book.author}</p>
                  )}
                  <p className="text-sm text-amber-700 mt-2 line-clamp-2">
                    {book.description}
                  </p>

                  {/* Stats */}
                  <div className="flex items-center gap-4 mt-4 pt-3 border-t border-amber-100 text-xs text-amber-600">
                    <span>{book.total_chapters} Chapters</span>
                    <span>{book.total_verses} Verses</span>
                    <span>{book.language}</span>
                  </div>

                  {/* Public Domain Badge */}
                  {book.source_info?.public_domain && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-green-700">
                      <span className="w-2 h-2 bg-green-500 rounded-full" />
                      Public Domain
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Legal Notice */}
        <div className="mt-12 p-4 bg-amber-50 rounded-xl border border-amber-200 text-center">
          <p className="text-amber-800 font-medium text-sm">📜 Legal Compliance Notice</p>
          <p className="text-amber-700 text-xs mt-2">
            All scriptures in this library are from <strong>public domain sources</strong>. 
            Sanskrit/Hindi original texts are ancient works without copyright restrictions.
            Translations are original work for DharmBook or from public domain translators.
          </p>
          <p className="text-amber-600 text-xs mt-2 italic">
            "We respect the sacred nature of these texts and present them for spiritual study."
          </p>
        </div>
      </div>
    </div>
  );
}
